import React from 'react';
import { useTheme } from '../../../theme/ThemeProvider';
import Plot from 'react-plotly.js';

const ChartElement = ({ 
  element, 
  processedData,  // Datos procesados del backend
  isEditing = false 
}) => {
  const { mode } = useTheme();
  const config = element.config || {};
  
  // Extraer datos procesados del backend
  const chartData = processedData?.data;
  const error = processedData?.error;
  const chartType = config.chartType || 'bar';
  
  const createTrace = (data) => {
    if (!data || !data.x) {
      return null;
    }
    
    // Caso especial: Histograma (solo necesita x)
    if (data.type === 'histogram' || chartType === 'histogram') {
      return {
        x: data.x,
        type: 'histogram',
        marker: {
          color: mode === 'dark' ? '#29B5E8' : '#1B96C7'
        },
        name: config.seriesName || 'Distribución'
      };
    }
    
    // Para otros tipos, necesitamos x e y
    if (!data.y) {
      return null;
    }
    
    const baseTrace = {
      x: data.x,
      y: data.y,
      type: getPlotlyType(chartType),
      name: config.seriesName || 'Serie 1',
      marker: {
        color: data.color || (mode === 'dark' ? '#29B5E8' : '#1B96C7'),
        line: {
          width: chartType === 'bar' ? 0 : 2
        }
      }
    };
    
    // Configuraciones específicas por tipo
    switch (chartType) {
      case 'pie':
        return {
          labels: data.x,
          values: data.y,
          type: 'pie',
          marker: {
            colors: generateColorPalette(data.x.length)
          },
          textinfo: 'label+percent',
          hoverinfo: 'label+value+percent'
        };
      
      case 'scatter':
        return {
          ...baseTrace,
          mode: 'markers',
          marker: {
            ...baseTrace.marker,
            size: 8,
            opacity: 0.7
          }
        };
      
      case 'line':
        return {
          ...baseTrace,
          mode: 'lines+markers',
          line: {
            width: 2,
            shape: 'spline'
          }
        };
      
      case 'area':
        return {
          ...baseTrace,
          fill: 'tozeroy',
          fillcolor: mode === 'dark' ? 'rgba(41, 181, 232, 0.2)' : 'rgba(27, 150, 199, 0.2)'
        };
      
      default:
        return baseTrace;
    }
  };
  
  const getPlotlyType = (chartType) => {
    const typeMap = {
      'bar': 'bar',
      'line': 'scatter',
      'scatter': 'scatter',
      'pie': 'pie',
      'area': 'scatter',
      'heatmap': 'heatmap',
      'box': 'box',
      'violin': 'violin'
    };
    return typeMap[chartType] || 'bar';
  };
  
  const generateColorPalette = (count) => {
    const baseColors = [
      '#29B5E8', '#38C172', '#FF6B35', '#8B5CF6', 
      '#F59E0B', '#EF4444', '#10B981', '#3B82F6',
      '#EC4899', '#F97316', '#14B8A6', '#8B5CF6'
    ];
    
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  };
  
  const getLayout = () => {
    const { 
      showLegend = true,
      legendPosition = 'top',
      xAxisTitle,
      yAxisTitle,
      showGrid = true
    } = config;
    
    return {
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: { 
        color: mode === 'dark' ? '#f6f8fb' : '#1a202c',
        family: 'Inter, system-ui, -apple-system, sans-serif'
      },
      margin: { t: 40, r: 20, b: 60, l: 60 },
      showlegend: showLegend,
      legend: {
        orientation: legendPosition === 'top' || legendPosition === 'bottom' ? 'h' : 'v',
        x: legendPosition === 'right' ? 1.02 : 0,
        y: legendPosition === 'top' ? 1.1 : (legendPosition === 'bottom' ? -0.2 : 1),
        xanchor: legendPosition === 'right' ? 'left' : 'left',
        yanchor: legendPosition === 'top' ? 'bottom' : 'top'
      },
      xaxis: {
        title: xAxisTitle || config.xColumn || '',
        gridcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        showgrid: showGrid,
        zeroline: false
      },
      yaxis: {
        title: yAxisTitle || config.yColumn || '',
        gridcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        showgrid: showGrid,
        zeroline: false
      },
      hovermode: 'closest'
    };
  };
  
  const styles = {
    container: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px'
    },
    placeholder: {
      textAlign: 'center',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
      padding: 'var(--spacing-xl)'
    },
    placeholderIcon: {
      fontSize: '48px',
      marginBottom: 'var(--spacing-md)',
      opacity: 0.5
    },
    placeholderText: {
      fontSize: 'var(--font-size-base)'
    },
    error: {
      color: '#FF6B35',
      padding: 'var(--spacing-md)',
      textAlign: 'center'
    }
  };
  
  // Renderizado condicional
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          ⚠️ {error}
        </div>
      </div>
    );
  }
  
  if (!processedData || !chartData) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <div style={styles.placeholderIcon}>📊</div>
          <div style={styles.placeholderText}>
            {isEditing ? 'Configura el gráfico' : 'Ejecutando...'}
          </div>
        </div>
      </div>
    );
  }
  
  const trace = createTrace(chartData);
  
  if (!trace) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <div style={styles.placeholderIcon}>⚙️</div>
          <div style={styles.placeholderText}>
            Sin datos para mostrar
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      <Plot
        data={[trace]}
        layout={getLayout()}
        config={{
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'],
          responsive: true
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default ChartElement;
